import React, { useEffect } from 'react'
import HomeTemplate from '../templates/HomeTemplate'
import useCanonicalUrl from '../hooks/useCanonicalUrl'
import './TermsOfService.css'

const TermsOfService = () => {
  useCanonicalUrl();
  useEffect(() => {
    document.title = 'Terms of Service - EveryKits';

    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Read the terms of service for using EveryKits free online tools, including usage rules, disclaimers, and limitations of liability.');
    }
  }, []);

  return (
    <HomeTemplate>
      <div className="terms-of-service">
        <h1>Terms of Service</h1>
        <p className="last-updated">Last updated: March 28, 2026</p>

        <section>
          <h2>Agreement to Terms</h2>
          <p>By accessing and using EveryKits ("the Service"), you agree to be bound by these Terms of Service ("Terms"). If you disagree with any part of these terms, you may not access the Service.</p>
        </section>

        <section>
          <h2>Description of Service</h2>
          <p>EveryKits provides free online tools including but not limited to:</p>
          <ul>
            <li>Age Calculator</li>
            <li>Word Counter</li>
            <li>Image Compressor and Converter</li>
            <li>Password Generator</li>
            <li>Percentage Calculator</li>
            <li>YouTube Thumbnail Downloader</li>
            <li>Text to Speech Converter</li>
            <li>QR Code Generator</li>
            <li>JSON Formatter</li>
          </ul>
          <p>All tools are provided free of charge and without warranty.</p>
        </section>

        <section>
          <h2>Acceptable Use</h2>
          <p>You agree to use our Service only for lawful purposes and in accordance with these Terms. You may not:</p>
          <ul>
            <li>Use the Service for any illegal or unauthorized purpose</li>
            <li>Attempt to gain unauthorized access to our systems</li>
            <li>Use automated tools to access our Service excessively</li>
            <li>Interfere with or disrupt the Service or servers</li>
            <li>Use the Service to transmit malicious code or viruses</li>
            <li>Violate any applicable laws or regulations</li>
            <li>Infringe on intellectual property rights</li>
          </ul>
        </section>

        <section>
          <h2>Intellectual Property Rights</h2>
          <p>The Service and its original content, features, and functionality are owned by EveryKits/TrainDevs and are protected by international copyright, trademark, and other intellectual property laws.</p>

          <h3>Your Content</h3>
          <p>You retain ownership of any content you input into our tools. However, by using our Service, you grant us a limited license to process your content solely for the purpose of providing the requested tool functionality.</p>
        </section>

        <section>
          <h2>Privacy and Data Protection</h2>
          <p>Your privacy is important to us. Please review our Privacy Policy, which also governs your use of the Service, to understand our practices.</p>
          <p>Most of our tools process data entirely in your browser. We do not store or retain your personal data, files, or results after your session ends.</p>
        </section>

        <section>
          <h2>Disclaimers</h2>
          <p>Our Service is provided on an "AS IS" and "AS AVAILABLE" basis. We make no representations or warranties of any kind, express or implied, including but not limited to:</p>
          <ul>
            <li>Accuracy, reliability, or availability of the Service</li>
            <li>Fitness for a particular purpose</li>
            <li>Non-infringement of intellectual property rights</li>
            <li>Security or absence of viruses or harmful components</li>
          </ul>
        </section>

        <section>
          <h2>Limitation of Liability</h2>
          <p>In no event shall EveryKits, its directors, employees, partners, or affiliates be liable for any indirect, incidental, special, consequential, or punitive damages, including without limitation, loss of profits, data, use, goodwill, or other intangible losses, resulting from:</p>
          <ul>
            <li>Your use or inability to use the Service</li>
            <li>Any unauthorized access to or use of our servers</li>
            <li>Any bugs, viruses, or harmful code that may be transmitted</li>
            <li>Errors or inaccuracies in content or calculations</li>
            <li>Any other matter relating to the Service</li>
          </ul>
        </section>

        <section>
          <h2>Service Availability</h2>
          <p>We strive to maintain the Service's availability but do not guarantee uninterrupted access. We may temporarily suspend or discontinue the Service for maintenance, updates, or other reasons without notice.</p>
        </section>

        <section>
          <h2>Third-Party Services</h2>
          <p>Our Service may contain links to third-party websites or services. We are not responsible for the content, privacy policies, or practices of third-party services.</p>

          <h3>Advertisements</h3>
          <p>We may display advertisements through Google AdSense. We are not responsible for the content or practices of advertisers. Your interaction with advertisers is solely between you and the advertiser.</p>
        </section>

        <section>
          <h2>User Responsibilities</h2>
          <p>You are responsible for:</p>
          <ul>
            <li>Maintaining the security of your device and internet connection</li>
            <li>Backing up any important data before using our tools</li>
            <li>Verifying the accuracy of tool results for critical applications</li>
            <li>Complying with all applicable laws when using our Service</li>
          </ul>
        </section>

        <section>
          <h2>Specific Tool Disclaimers</h2>
          <h3>Financial Tools</h3>
          <p>Our financial calculators (like the Percentage Calculator) are for informational purposes only and should not be used for making financial decisions without professional consultation.</p>

          <h3>Age Calculator</h3>
          <p>The Age Calculator provides estimates based on the information provided. Results may not be accurate for legal or official purposes.</p>

          <h3>File Processing Tools</h3>
          <p>While we take precautions with file processing, always backup important files before using compression or conversion tools.</p>
        </section>

        <section>
          <h2>Termination</h2>
          <p>We may terminate or suspend your access to the Service immediately, without prior notice, for any reason, including without limitation if you breach the Terms.</p>
        </section>

        <section>
          <h2>Governing Law</h2>
          <p>These Terms shall be interpreted and governed by the laws of the jurisdiction in which EveryKits operates, without regard to conflict of law provisions.</p>
        </section>

        <section>
          <h2>Changes to Terms</h2>
          <p>We reserve the right to modify these Terms at any time. If we make material changes, we will notify you by email or by posting a notice on our website prior to the effective date of the changes.</p>
        </section>

        <section>
          <h2>Contact Information</h2>
          <p>If you have any questions about these Terms of Service, please contact us:</p>
          <ul>
            <li>Email: legal@everykits.com</li>
            <li>Website: everykits.com/contact</li>
          </ul>
        </section>
      </div>
    </HomeTemplate>
  )
}

export default TermsOfService